import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { getTopProductMargins } from "../../slices/thunks";
import { createSelector } from "reselect";
import Flatpickr from "react-flatpickr";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import {
  DASHBOARD_DATE_FILTER_PERIODS,
  getDashboardDateRange,
} from "./dateFilterUtils";
import { toChartLabel, toChartNumber } from "./dashboardChartUtils";

const TopProductMargins = ({ dateFilter = null }) => {
  const dispatch = useDispatch();
  const dateFrom = useRef(null);
  const dateTo = useRef(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const isGlobalDateFilter = Boolean(dateFilter);

  const selectDashboardData = createSelector(
    (state) => state.Dashboard,
    (dashboard) => dashboard
  );

  const { topProductMargins } = useSelector(selectDashboardData);

  const [state, setState] = useState({
    series: [],
    options: {
      chart: {
        type: "bar",
        height: 350,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          horizontal: true,
        },
      },
      dataLabels: { enabled: false },
      tooltip: {
        y: {
          formatter: (val) => `${toChartNumber(val).toFixed(2)}%`,
        },
      },
      xaxis: {
        categories: [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: true },
        labels: {
          show: true, // categorías son los canales; no agregamos "%"
        },
      },
      yaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { show: true },
      },
      annotations: {
        xaxis: [], // se completa cuando hay promedio general
      },
    },
  });

  const getData = () => {
    const data = {
      dateFrom:
        dateFrom.current != null
          ? moment(dateFrom.current).format("YYYY-MM-DD")
          : null,
      dateTo:
        dateTo.current != null
          ? moment(dateTo.current).format("YYYY-MM-DD")
          : null,
    };
    dispatch(getTopProductMargins(data));
  };

  useEffect(() => {
    const items = topProductMargins?.averageMarginsPerChannel ?? [];

    if (!items.length) {
      setState((prev) => ({
        ...prev,
        series: [],
        options: {
          ...prev.options,
          // Las categorías para barras horizontales deben ir en xaxis.categories
          xaxis: { ...prev.options.xaxis, categories: [], min: 0, max: undefined },
          annotations: { xaxis: [] },
        },
      }));
      return;
    }

    // Orden y parseo numérico seguro
    const sorted = [...items].sort(
      (a, b) =>
        toChartNumber(b.averageNetProfitMargin) -
        toChartNumber(a.averageNetProfitMargin)
    );

    const categories = sorted.map((i) => toChartLabel(i.salesChannel));
    const values = sorted.map((i) => toChartNumber(i.averageNetProfitMargin));

    // Overall del backend o fallback promedio simple
    let overall = Number(topProductMargins?.averageNetProfitMarginOverall);
    if (!Number.isFinite(overall)) {
      overall = values.reduce((acc, v) => acc + v, 0) / values.length;
    }

    // Asegurar que el eje X cubra el overall (padding 5%)
    const maxVal = Math.max(...values);
    const xMax = Math.max(maxVal, overall);
    const paddedMax = xMax === 0 ? 1 : xMax * 1.05; // evita max=0

    setState((prev) => ({
      ...prev,
      series: [
        { name: "Average Net Profit Margin (%)", type: "bar", data: values },
      ],
      options: {
        ...prev.options,
        // En ApexCharts, incluso con barras horizontales, las categorías se definen en xaxis.categories
        // y el eje X sigue siendo el numérico (valores). Esto muestra categories en el eje Y visualmente.
        xaxis: {
          ...prev.options.xaxis,
          categories,
          min: 0,
          max: paddedMax,
          labels: {
            ...prev.options.xaxis.labels,
            formatter: (val) => `${toChartNumber(val).toFixed(2)}%`,
          },
        },
        // yaxis se mantiene para estilos; no debe contener categories
        yaxis: {
          ...prev.options.yaxis,
        },
        tooltip: {
          y: {
            formatter: (val) => `${toChartNumber(val).toFixed(2)}%`,
          },
        },
        annotations: {
          xaxis: Number.isFinite(overall)
            ? [
                {
                  x: overall,
                  borderColor: "#888",
                  strokeDashArray: 4,
                  position: "front",
                  label: {
                    text: `Overall ${overall.toFixed(2)}%`,
                    style: { fontSize: "12px" },
                  },
                },
              ]
            : [],
        },
      },
    }));
  }, [topProductMargins]);

  useEffect(() => {
    if (!isGlobalDateFilter) {
      return;
    }

    const range = getDashboardDateRange(
      dateFilter.period,
      dateFilter.customDateFrom,
      dateFilter.customDateTo
    );

    dateFrom.current = range.dateFrom;
    dateTo.current = range.dateTo;

    if (
      dateFilter.period !== DASHBOARD_DATE_FILTER_PERIODS.CUSTOM ||
      range.dateFrom ||
      range.dateTo
    ) {
      getData();
    }
  }, [
    dateFilter?.customDateFrom,
    dateFilter?.customDateTo,
    dateFilter?.period,
    isGlobalDateFilter,
  ]);

  useEffect(() => {
    if (isGlobalDateFilter) {
      return;
    }

    switch (selectedPeriod) {
      case "7 days": {
        const date = new Date();
        const sevenDaysAgo = new Date(date);
        sevenDaysAgo.setDate(date.getDate() - 7);
        dateFrom.current = sevenDaysAgo;
        dateTo.current = new Date();
        break;
      }
      case "30 days": {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(new Date().getDate() - 30);
        dateFrom.current = thirtyDaysAgo;
        dateTo.current = new Date();
        break;
      }
      case "all":
        dateFrom.current = null;
        dateTo.current = null;
        break;
      case "custom":
        // esperamos a que Flatpickr cambie y dispare getData()
        break;
      default:
        break;
    }
    if (selectedPeriod !== "custom") getData();
  }, [isGlobalDateFilter, selectedPeriod]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            Average Margins Per Channel
          </h4>
          {!isGlobalDateFilter && <div className="d-flex gap-1">
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "all" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => setSelectedPeriod("all")}
            >
              ALL
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "7 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => setSelectedPeriod("7 days")}
            >
              7 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "30 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => setSelectedPeriod("30 days")}
            >
              30 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "custom" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => setSelectedPeriod("custom")}
            >
              Custom
            </button>

            {selectedPeriod === "custom" && (
              <div className="d-flex align-items-center gap-2 mt-2">
                <Flatpickr
                  className="form-control"
                  placeholder="Select a date from"
                  onChange={(selectedDates) => {
                    dateFrom.current = selectedDates[0] || null;
                    if (dateTo.current || selectedDates[0]) getData();
                  }}
                  options={{
                    maxDate: dateTo.current || undefined,
                    mode: "single",
                    dateFormat: "Y-m-d",
                  }}
                />
                <span>to</span>
                <Flatpickr
                  className="form-control"
                  placeholder="Select a date to"
                  onChange={(selectedDates) => {
                    dateTo.current = selectedDates[0] || null;
                    if (dateFrom.current || selectedDates[0]) getData();
                  }}
                  options={{
                    mode: "single",
                    minDate: dateFrom.current || undefined,
                    dateFormat: "Y-m-d",
                  }}
                />
              </div>
            )}
          </div>}
        </CardHeader>

        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              {topProductMargins ? (
                <ReactApexChart
                  options={state.options}
                  series={state.series}
                  type="bar"
                  height={350}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default TopProductMargins;
